import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiClock, FiTarget, FiChevronUp, FiChevronDown, FiActivity, FiX } from 'react-icons/fi';
import { usePerformanceTask } from '../../../contexts/PerformanceTaskContext';

const PerformanceTaskFloatingWidget = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTasks, isWidgetOpen, toggleWidget } = usePerformanceTask();
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);

  // Don't show on the specific task page to avoid clutter
  // Also hide if no active tasks
  if (activeTasks.length === 0 || location.pathname.includes('/performance-tasks/')) {
    return null;
  }

  const currentTask = activeTasks[currentTaskIndex];

  const handleNextTask = (e) => {
    e.stopPropagation();
    setCurrentTaskIndex((prev) => (prev + 1) % activeTasks.length);
  };

  const handlePrevTask = (e) => {
    e.stopPropagation();
    setCurrentTaskIndex((prev) => (prev - 1 + activeTasks.length) % activeTasks.length);
  };

  const formatTimeRemaining = (endTime) => {
    const end = new Date(endTime);
    const now = new Date();
    const diff = end - now;

    if (diff <= 0) return '00:00:00';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const calculateProgress = (task) => {
    if (!task) return 0;
    const progress = parseFloat(task.progress_percentage || 0);
    return Math.min(Math.max(progress, 0), 100);
  };

  return (
    <div className={`fixed bottom-4 right-4 z-50 transition-all duration-300 ${isWidgetOpen ? 'w-80' : 'w-16 h-16 rounded-full'}`}>
      {/* Toggle Button (When Closed) */}
      {!isWidgetOpen && (
        <button
          onClick={toggleWidget}
          className="w-16 h-16 bg-accent-gold rounded-full shadow-lg flex items-center justify-center text-primary-black hover:bg-accent-goldDark transition-colors relative"
        >
          <FiActivity className="w-8 h-8" />
          {activeTasks.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-white">
              {activeTasks.length}
            </span>
          )}
        </button>
      )}

      {/* Expanded Panel */}
      {isWidgetOpen && (
        <div className="bg-white rounded-lg shadow-xl overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-primary-black text-white px-4 py-3 flex items-center justify-between cursor-pointer" onClick={toggleWidget}>
            <div className="flex items-center gap-2">
              <FiActivity className="text-accent-gold" />
              <span className="font-medium text-sm">Active Tasks ({activeTasks.length})</span>
            </div>
            <button className="text-gray-400 hover:text-white">
              <FiChevronDown />
            </button>
          </div>

          {/* Body */}
          <div 
            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => navigate(`/performance-tasks/${currentTask.id}`)}
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-semibold text-gray-900 text-sm line-clamp-1">{currentTask.title}</h4>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                currentTask.status === 'active' ? 'bg-green-100 text-green-800' :
                currentTask.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {currentTask.status}
              </span>
            </div>

            {/* Timer */}
            <div className="flex items-center gap-2 text-gray-600 text-xs mb-3">
              <FiClock className="w-3 h-3" />
              <span className="font-mono font-medium text-red-600">
                {currentTask.status === 'active' ? (
                  <CountdownTimer endTime={currentTask.extended_end_time || currentTask.end_time} />
                ) : currentTask.status === 'scheduled' ? (
                  <CountdownTimer endTime={currentTask.start_time} />
                ) : (
                  '--:--:--'
                )}
              </span>
              <span className="text-gray-400">
                {currentTask.status === 'scheduled' ? 'to start' : 'remaining'}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="mb-1">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Progress</span>
                <span className="font-medium">{calculateProgress(currentTask).toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-accent-gold h-2 rounded-full transition-all duration-500"
                  style={{ width: `${calculateProgress(currentTask)}%` }}
                ></div>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 mt-2 flex justify-between">
               <span>Target: {parseFloat(currentTask.target_value).toLocaleString()}</span>
               <span>Current: {parseFloat(currentTask.current_progress || 0).toLocaleString()}</span>
            </div>
          </div>

          {/* Footer / Navigation */}
          {activeTasks.length > 1 && (
            <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-t border-gray-100">
              <button 
                onClick={handlePrevTask}
                className="text-gray-500 hover:text-primary-black p-1"
              >
                <span className="text-xs">Prev</span>
              </button>
              <span className="text-xs text-gray-400">
                {currentTaskIndex + 1} / {activeTasks.length}
              </span>
              <button 
                onClick={handleNextTask}
                className="text-gray-500 hover:text-primary-black p-1"
              >
                <span className="text-xs">Next</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper component for live countdown
const CountdownTimer = ({ endTime }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const calculateTime = () => {
      const end = new Date(endTime);
      const now = new Date();
      const diff = end - now;

      if (diff <= 0) {
        setTimeLeft('00:00:00');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    };

    calculateTime();
    const timer = setInterval(calculateTime, 1000);

    return () => clearInterval(timer);
  }, [endTime]);

  return <>{timeLeft}</>;
};

export default PerformanceTaskFloatingWidget;
